import type { AcceptedApplicantData, CohortData } from '@platform/client/schemas';
import { Icon } from '@platform/components';
import cx from 'classix';
import { useMemo, useState } from 'react';
import { formatDateStr } from '../../../dashboard/my-projects/utils';
import type { AcceptedGroup } from '../types';
import { NameCell } from '../NameCell';
import { ProjectLink } from './ProjectLink';
import {
  capitalize,
  groupAcceptedByMatch,
  sortAcceptedGroups,
  type AcceptedSortColumn,
  type SortDirection,
} from './utils';

const SortableHeader = ({
  label,
  column,
  activeColumn,
  direction,
  onSort,
  className,
}: {
  label: string;
  column: AcceptedSortColumn;
  activeColumn: AcceptedSortColumn | null;
  direction: SortDirection;
  onSort: (column: AcceptedSortColumn) => void;
  className: string;
}) => {
  const isActive = activeColumn === column;
  return (
    <th className={className}>
      <div
        className='flex items-center gap-1 cursor-pointer select-none'
        onClick={() => onSort(column)}
      >
        {label}
        {isActive && direction === 'asc' ?
          <Icon type='chevron-up' size='12' />
        : isActive && direction === 'desc' ?
          <Icon type='chevron-down' size='12' />
        : <span
            className='inline-flex flex-col items-center justify-center'
            aria-hidden='true'
          >
            <Icon type='chevron-up' size='12' />
            <Icon type='chevron-down' size='12' additionalClassNames='-mt-1' />
          </span>}
      </div>
    </th>
  );
};

export function AcceptedStatusTable({
  data,
  cohort,
}: {
  data: AcceptedApplicantData[];
  cohort: CohortData;
}) {
  const isTeamsCohort = cohort.type === 'Teams';
  const groups = useMemo<AcceptedGroup[]>(
    () =>
      isTeamsCohort
        ? groupAcceptedByMatch(data)
        : data.map((row, idx) => ({
            key: String(idx),
            rows: [row],
            match: row.matchData,
            matchStatus: row.matchStatus,
          })),
    [data, isTeamsCohort],
  );

  const [sortCol, setSortCol] = useState<AcceptedSortColumn | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>(null);

  const handleSort = (column: AcceptedSortColumn) => {
    if (sortCol !== column) {
      setSortCol(column);
      setSortDir('asc');
      return;
    }
    if (sortDir === 'asc') {
      setSortDir('desc');
      return;
    }
    setSortCol(null);
    setSortDir(null);
  };

  const sortedGroups = useMemo(
    () => sortAcceptedGroups(groups, sortCol, sortDir),
    [groups, sortCol, sortDir],
  );
  const headerCellClass =
    'border-l border-gray-100 px-4 align-middle text-md font-semibold text-gray-500';

  return (
    <div className='mt-4 overflow-hidden rounded-xl border border-gray-100 bg-white'>
      {data.length === 0 ? (
        <div className='px-4 py-8 text-center text-sm text-gray-400'>
          No applicants in this group
        </div>
      ) : (
        <table className='w-full border-collapse text-left text-sm'>
            <thead className='sticky top-0 z-10 bg-gray-50'>
              <tr className='h-[75px]'>
                <SortableHeader
                  label='Participant Name'
                  column='name'
                  activeColumn={sortCol}
                  direction={sortDir}
                  onSort={handleSort}
                  className='pl-4 align-middle text-md font-semibold text-gray-500'
                />
                <SortableHeader
                  label='Matched To'
                  column='project'
                  activeColumn={sortCol}
                  direction={sortDir}
                  onSort={handleSort}
                  className={headerCellClass}
                />
                <SortableHeader
                  label='Start Date'
                  column='startDate'
                  activeColumn={sortCol}
                  direction={sortDir}
                  onSort={handleSort}
                  className={headerCellClass}
                />
                <SortableHeader
                  label='End Date'
                  column='endDate'
                  activeColumn={sortCol}
                  direction={sortDir}
                  onSort={handleSort}
                  className={headerCellClass}
                />
                <SortableHeader
                  label='Project Progress'
                  column='status'
                  activeColumn={sortCol}
                  direction={sortDir}
                  onSort={handleSort}
                  className={headerCellClass}
                />
              </tr>
            </thead>
            <tbody>
              {sortedGroups.map((group, gIdx) => {
                const tint =
                  isTeamsCohort && gIdx % 2 === 1
                    ? 'bg-secondary-brand-25'
                    : '';
                return group.rows.map((row, idx) => {
                  const isFirst = idx === 0;
                  return (
                    <tr key={row.id} className={cx(tint, isFirst && 'border-t border-gray-100')}>
                      <td
                        className={cx(
                          'py-3 pl-4 align-middle',
                          !isFirst && 'border-t border-gray-100',
                        )}
                      >
                        <NameCell name={row.name} email={row.email} />
                      </td>
                      {isFirst && (
                        <>
                          <td
                            rowSpan={group.rows.length}
                            className='border-l border-gray-100 px-4 align-middle'
                          >
                            {group.match ? (
                              <ProjectLink
                                name={group.match.projectName}
                                projectId={group.match.projectId}
                              />
                            ) : null}
                          </td>
                          <td
                            rowSpan={group.rows.length}
                            className='border-l border-gray-100 px-4 align-middle'
                          >
                            {group.match ? formatDateStr(group.match.projectStartDate) : null}
                          </td>
                          <td
                            rowSpan={group.rows.length}
                            className='border-l border-gray-100 px-4 align-middle'
                          >
                            {group.match ? formatDateStr(group.match.projectEndDate) : null}
                          </td>
                          <td
                            rowSpan={group.rows.length}
                            className='border-l border-gray-100 px-4 align-middle'
                          >
                            {group.matchStatus ? capitalize(group.matchStatus) : null}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>
      )}
    </div>
  );
}
